<template>
  <div class="department-summary">
    <v-card flat v-if="isLoading">
      <v-card-text>
        <v-progress-linear :indeterminate="true"></v-progress-linear>
      </v-card-text>
    </v-card>

    <div v-else>
      <v-card flat>
        <ReportViewBar
          v-if="Payment"
          :title="`Department Summary`"
          :start="start"
          :end="end"
        />

        <v-container fluid>
          <!-- ← New select for categories -->
          <v-select
            :items="categoryOptions"
            item-text="name"
            item-value="id"
            v-model="selectedCategory"
            label="Filter by Category"
            dense
            outlined
            class="mb-4"
          />

          <v-card outlined flat v-show="!isLoading">
            <div ref="pieContainer" style="width: 100%; height: 300px"></div>
          </v-card>

          <table class="center">
            <tbody>
              <v-card-text>
                <v-data-table
                  dense
                  sort-by="date"
                  readonly
                  :headers="computedHeaders"
                  :itemsPerPage="Payment.length"
                  hide-default-footer
                  :items="Payment"
                >
                  <template v-slot:item="{ item }">
                    <tr>
                      <td
                        class="text-left text-capitalize"
                        v-if="selectedCategory === 0"
                      >
                        {{ item.donationCategoryName }}
                      </td>
                      <td class="text-left">
                        {{ item.productName }}
                      </td>
                      <td class="text-right">
                        {{ item.totalRevenue | currency }}
                      </td>
                    </tr>
                  </template>
                  <template
                    v-slot:body.append="{ headers }"
                    v-if="Payment.length > 0"
                  >
                    <tr
                      :style="{ 'background-color': $style.listTotal.filter }"
                    >
                      <td
                        :colspan="headers.length - 1"
                        class="text-right font-weight-bold"
                      >
                        Total
                      </td>
                      <td class="text-right font-weight-bold">
                        {{ total | currency }}
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card-text>
            </tbody>
          </table>
        </v-container>
      </v-card>
    </div>
  </div>
</template>

<script>
import ReportViewBar from "@/components/ReportViewBar";
import paymentService from "@/modules/Payment/service";
import donationCategoryService from "@/modules/DonationCategory/service";
import Highcharts from "highcharts";

export default {
  components: { ReportViewBar },

  data() {
    return {
      start: null,
      end: null,
      isLoading: true,
      Payment: [],

      donationCategories: [],
      selectedCategory: 0,
      headers: [
        {
          text: "CATEGORY",
          align: "left",
          value: "donationCategoryName",
          sortable: true,
        },
        {
          text: "DEPARTMENT",
          align: "left",
          value: "productName",
          sortable: true,
        },
        {
          text: "AMOUNT",
          align: "right",
          value: "totalRevenue",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    computedHeaders() {
      // when “All” is selected (0), we show all three; otherwise drop the CATEGORY column
      return this.selectedCategory === 0
        ? this.headers
        : this.headers.filter((h) => h.value !== "donationCategoryName");
    },
    total() {
      return this.Payment.reduce((sum, r) => sum + r.totalRevenue, 0);
    },
    categoryOptions() {
      return [{ id: 0, name: "All" }, ...this.donationCategories];
    },
  },

  watch: {
    selectedCategory: "departmentSummary",
  },

  async created() {
    this.start = this.$route.query.start;
    this.end = this.$route.query.end;
    await this.fetchDonationCategories();
    await this.departmentSummary();
  },

  methods: {
    renderPie() {
      // rename to renderChart if you like, but keeping the same name here
      const categories = this.Payment.map((row) => row.productName);
      const values = this.Payment.map((row) => row.totalRevenue);

      Highcharts.chart(this.$refs.pieContainer, {
        credits: { enabled: false },
        chart: { type: "column" }, // ← column = vertical bars
        title: { text: null },
        xAxis: {
          categories,
          title: { text: "Department" },
        },
        yAxis: {
          title: { text: "Total Revenue" },
        },
        tooltip: {
          pointFormat: "Amount: <b>${point.y:.1f}</b>",
        },
        plotOptions: {
          column: {
            dataLabels: {
              enabled: true,
              format: "${point.y:.2f}", // ← show the amount on each bar
            },
          },
        },
        colors: [
          "#7cb5ec",
          "#434348",
          "#90ed7d",
          "#f7a35c",
          "#8085e9",
          "#f15c80",
          "#e4d354",
          "#2b908f",
          "#f45b5b",
          "#91e8e1",
        ], // ← optional custom palette
        series: [
          {
            name: "Revenue",
            data: values,
            colorByPoint: true 
          },
        ],
      });
    },
    async fetchDonationCategories() {
      try {
        const resp = await donationCategoryService.getAll();
        this.donationCategories = resp.data;
      } catch (err) {
        console.error("Failed to load categories", err);
      }
    },

    async departmentSummary() {
      this.isLoading = true;
      const where = {
        start: this.start,
        end: this.end,
        donationCategoryId: this.selectedCategory,
      };

      try {
        const { data } = await paymentService.departmentSummary(where);
        this.Payment = data.filter(item => item.productName !== null);
        this.isLoading = false;
        this.$nextTick(() => {
          this.renderPie();
        });
      } catch (err) {
        console.error("error in departmentSummary", err);
      } finally {
        this.isLoading = false;
        this.$Progress.finish();
      }
    },
  },
};
</script>


<style scoped>
@media print {
  container {
    height: 100%;
    margin: 0 !important;
    padding: 0 !important;
    overflow: hidden;
  }
  .no-print {
    display: none;
  }
  .detailBox {
    overflow: visible;
    height: 100%;
  }
  * {
    background: 0 0 !important;
    color: #000 !important;
    box-shadow: none !important;
    text-shadow: none !important;
  }
}
.paidImg {
  position: absolute;
  left: 40%;
  top: 10%;
}

.signature {
  margin-top: 100px;
}
.center {
  background-color: white;
  margin: auto;
  width: 100%;
}
/* @media print {
  .detailBox * {
    overflow: visible;
    height: 100%;
    page-break-before: always;
  }
}
.detailBox {
  overflow: auto;
  height: 600px;
} */
</style>
